import { useEffect, useRef, type ReactNode } from 'react';
import { Box, Button, Icon, Text, Tooltip } from '@chakra-ui/react';
import { ListChecks } from 'lucide-react';
import { t } from '@lingui/core/macro';

import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import { markingStatus } from './utils';
import type { AssessorSlide } from './types';

const DOT_COLORS = {
  CORRECT: { bg: 'green.500', border: 'green.500' },
  REVISION: { bg: 'red.400', border: 'red.400' },
  UNAVAILABLE: { bg: 'transparent', border: 'fg.muted' },
};

function PillButton({
  dotCorrect,
  isSelected,
  isDisabled,
  onClick,
  tooltipLabel,
  ariaLabel,
  children,
}: {
  dotCorrect: boolean | null;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
  tooltipLabel: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const dot = DOT_COLORS[markingStatus(dotCorrect)];

  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [isSelected]);

  let hoverBorderColor = 'border.emphasis';
  if (isDisabled) {
    hoverBorderColor = 'border.default';
  } else if (isSelected) {
    hoverBorderColor = 'purple.500';
  }

  return (
    <Tooltip
      label={
        <Text noOfLines={4} whiteSpace="pre-wrap">
          {tooltipLabel}
        </Text>
      }
      maxW={pxToRem(320)}
      placement="top"
      hasArrow
      openDelay={150}
    >
      <Button
        ref={ref}
        size="sm"
        variant="unstyled"
        display="inline-flex"
        alignItems="center"
        gap={2}
        rounded="full"
        bg="bg.surface"
        borderWidth="1px"
        borderColor={isSelected ? 'purple.500' : 'border.default'}
        color={isSelected ? 'purple.500' : 'fg.default'}
        opacity={isDisabled ? 0.4 : 1}
        height="8"
        px={3}
        fontWeight="medium"
        fontSize="sm"
        flexShrink={0}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={isDisabled ? undefined : onClick}
        aria-current={isSelected ? 'true' : undefined}
        aria-disabled={isDisabled ? 'true' : undefined}
        aria-label={ariaLabel}
        _hover={{ borderColor: hoverBorderColor }}
      >
        <Box
          as="span"
          w={pxToRem(8)}
          h={pxToRem(8)}
          rounded="full"
          bg={dot.bg}
          borderWidth="1.5px"
          borderColor={dot.border}
          aria-hidden
        />
        {children}
      </Button>
    </Tooltip>
  );
}

export function QuestionPill({
  slide,
  isSelected,
  isDisabled,
  onClick,
}: {
  slide: AssessorSlide;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) {
  const { title, questionLabelPlainText, correct, displayLabel } = slide;
  const tooltipLabel = [title.trim(), questionLabelPlainText.trim()]
    .filter(({ length }) => length > 0)
    .join(' - ');

  return (
    <PillButton
      dotCorrect={correct ?? null}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onClick={onClick}
      tooltipLabel={tooltipLabel}
      ariaLabel={`Question ${displayLabel}`}
    >
      <Text as="span" lineHeight="1">
        {displayLabel}
      </Text>
    </PillButton>
  );
}

export function SummaryPill({
  isSelected,
  isDisabled,
  outcomeCorrect,
  onClick,
}: {
  isSelected: boolean;
  isDisabled: boolean;
  outcomeCorrect: boolean | null;
  onClick: () => void;
}) {
  return (
    <PillButton
      dotCorrect={outcomeCorrect}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onClick={onClick}
      tooltipLabel={t`Summary`}
      ariaLabel={t`Summary`}
    >
      <Icon as={ListChecks} boxSize={iconSize.md} />
    </PillButton>
  );
}
